/**
 * The time tracker: a timer on an engagement, time entered afterwards, and the running timer shown
 * in the header wherever you are. Switched on in Firm Settings; the hours feed Firm Analytics.
 * Also the dollar field used for quoted and monthly fees.
 */
import { formatCents, formatMinutes, parseDollars } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, Plus, Square, Timer, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Empty } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate, localToday } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { cn } from "@/lib/utils";

type RunningEntry = { id: string; engagementId: string; engagementName: string; clientName: string; startedAt: string; note: string };
type Running = { enabled: boolean; running: RunningEntry | null; now: string };
type TimeEntry = { id: string; userId: string; userName: string; workDate: string; minutes: number; note: string; startedAt: string | null; running: boolean; canEdit: boolean };
type EngagementTimeData = { enabled: boolean; totalMinutes: number; byPerson: Array<{ userId: string; name: string; minutes: number }>; entries: TimeEntry[] };

const RUNNING_KEY = ["/api/time/running"];

/** Minutes since a timer started, ticking every second while shown. */
function useElapsed(startedAt: string | null, serverNow?: string) {
  // The server's clock and this device's may differ: measure from the gap when the server last answered.
  const [skew, setSkew] = useState(0);
  useEffect(() => {
    if (serverNow) setSkew(Date.parse(serverNow) - Date.now());
  }, [serverNow]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  void tick;
  if (!startedAt) return 0;
  return Math.max(0, Date.now() + skew - Date.parse(startedAt));
}

function clock(ms: number) {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function useTimerActions() {
  const qc = useQueryClient();
  const done = (engagementId?: string) => {
    qc.invalidateQueries({ queryKey: RUNNING_KEY });
    qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/engagements/") && (!engagementId || (q.queryKey[0] as string).includes(engagementId)) });
  };
  const start = useMutation({
    meta: { inlineError: true },
    mutationFn: (engagementId: string) => api.send<{ capped: boolean }>("POST", "/api/time/start", { engagementId }),
    onSuccess: () => done(),
  });
  const stop = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ capped: boolean }>("POST", "/api/time/stop", {}),
    onSuccess: () => done(),
  });
  return { start, stop };
}

/** The running timer, in the header: what it's on, how long, and Stop. */
export function RunningTimer() {
  const { data } = useQuery<Running>({ queryKey: RUNNING_KEY, refetchInterval: 60_000 });
  const { stop } = useTimerActions();
  const r = data?.running ?? null;
  const elapsed = useElapsed(r?.startedAt ?? null, data?.now);
  if (!r) return null;
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-full border border-primary/30 bg-primary/5 py-1 pl-3 pr-1 text-xs">
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <Link href={`/engagements/${r.engagementId}`} className="min-w-0 truncate font-medium text-foreground hover:underline" title={`${r.clientName} · ${r.engagementName}`}>
        <span className="hidden sm:inline">{r.clientName} · </span>
        <span className="tabular">{clock(elapsed)}</span>
      </Link>
      <button type="button" onClick={() => stop.mutate()} disabled={stop.isPending} className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 font-medium text-primary-foreground hover:opacity-90" aria-label="Stop the timer">
        <Square className="h-3 w-3 fill-current" /> Stop
      </button>
      {stop.error && (
        <span role="alert" className="pr-2 text-destructive">
          {errorMessage(stop.error)}
        </span>
      )}
    </div>
  );
}

/** "1:30", "1h 30m", "90" (minutes), "1.5" (hours) → minutes. */
export function parseDuration(v: string): number | null {
  const t = v.trim().toLowerCase();
  if (!t) return null;
  let m = /^(\d+):(\d{1,2})$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = /^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/.exec(t);
  if (m && (m[1] || m[2])) return Math.round(Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0));
  if (/^\d+(\.\d+)?$/.test(t)) return Number(t) <= 12 && t.includes(".") ? Math.round(Number(t) * 60) : Math.round(Number(t));
  return null;
}

/** An engagement's time: its timer, the entries, and time entered afterwards. */
export function EngagementTime({ engagementId, me, open }: { engagementId: string; me: Me; open: boolean }) {
  const qc = useQueryClient();
  const key = [`/api/engagements/${engagementId}/time`];
  const { data } = useQuery<EngagementTimeData>({ queryKey: key });
  const { data: running } = useQuery<Running>({ queryKey: RUNNING_KEY });
  const { start, stop } = useTimerActions();
  const canLog = hasPermission(me, "tasks.write");
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState(localToday());
  const [dur, setDur] = useState("");
  const [note, setNote] = useState("");
  const minutes = parseDuration(dur);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const add = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", "/api/time/entries", { engagementId, workDate: date, minutes, note: note || undefined }),
    onSuccess: () => {
      setDur("");
      setNote("");
      setAdding(false);
      refresh();
    },
  });
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send("DELETE", `/api/time/entries/${id}`), onSuccess: refresh });
  if (!data) return null;
  if (!data.enabled && data.entries.length === 0) return null;
  const mine = running?.running?.engagementId === engagementId ? running.running : null;
  const elsewhere = running?.running && !mine ? running.running : null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="tabular text-2xl font-semibold">{formatMinutes(data.totalMinutes)}</p>
          <p className="text-xs text-muted-foreground">
            {data.byPerson.length ? data.byPerson.map((p) => `${p.name} ${formatMinutes(p.minutes)}`).join(" · ") : "No time logged yet"}
          </p>
        </div>
        {data.enabled && canLog && (
          <div className="flex flex-wrap gap-2">
            {mine ? (
              <Button size="sm" onClick={() => stop.mutate()} disabled={stop.isPending}>
                <Square className="h-3.5 w-3.5 fill-current" /> Stop timer
              </Button>
            ) : (
              open && (
                <Button size="sm" onClick={() => start.mutate(engagementId)} disabled={start.isPending} title={elsewhere ? `Stops your timer on ${elsewhere.clientName}` : undefined}>
                  <Play className="h-3.5 w-3.5 fill-current" /> Start timer
                </Button>
              )
            )}
            <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
              <Plus className="h-3.5 w-3.5" /> Add time
            </Button>
          </div>
        )}
      </div>
      {elsewhere && !mine && data.enabled && <p className="text-xs text-muted-foreground">Your timer is running on {elsewhere.clientName}. Starting one here stops it and saves its time.</p>}
      {(start.error || stop.error) && <p className="text-sm text-destructive">{errorMessage(start.error ?? stop.error)}</p>}
      {adding && (
        <form
          className="grid gap-3 rounded-md border border-border bg-muted/30 p-3 sm:grid-cols-[9rem_7rem_1fr_auto] sm:items-end"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (minutes) add.mutate();
          }}
        >
          <label className="space-y-1.5 text-sm">
            <span className="block text-xs font-medium text-muted-foreground">Date</span>
            <Input type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="block text-xs font-medium text-muted-foreground">Time spent</span>
            <Input value={dur} onChange={(e) => setDur(e.target.value)} placeholder="1:30" aria-describedby="dur-help" required />
          </label>
          <label className="space-y-1.5 text-sm">
            <span className="block text-xs font-medium text-muted-foreground">What you did (optional)</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          <Button type="submit" disabled={!minutes || add.isPending}>
            Save
          </Button>
          <p id="dur-help" className="text-xs text-muted-foreground sm:col-span-4">
            {dur && !minutes ? "Enter hours and minutes, like 1:30 or 45m." : minutes ? `${formatMinutes(minutes)} on ${formatDate(date)}` : "Hours and minutes, like 1:30, 2h or 45m."}
          </p>
          {add.error && <p className="text-sm text-destructive sm:col-span-4">{errorMessage(add.error)}</p>}
        </form>
      )}
      {data.entries.length === 0 ? (
        <Empty>No time logged on this engagement yet.</Empty>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border text-sm">
          {data.entries.slice(0, 50).map((t) => (
            <li key={t.id} className="flex items-center gap-3 px-3 py-2">
              <Timer className={cn("h-4 w-4 shrink-0", t.running ? "text-primary" : "text-muted-foreground/60")} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  <span className="font-medium">{t.userName}</span>
                  {t.note && <span className="text-muted-foreground"> · {t.note}</span>}
                </p>
                <p className="text-xs text-muted-foreground">{formatDate(t.workDate)}{t.startedAt ? " · timer" : ""}</p>
              </div>
              <span className="tabular shrink-0 font-medium">{t.running ? "Running" : formatMinutes(t.minutes)}</span>
              {t.canEdit && !t.running && (
                <button type="button" className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label="Remove this time" onClick={() => remove.mutate(t.id)} disabled={remove.isPending}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {remove.error && <p className="text-sm text-destructive">{errorMessage(remove.error)}</p>}
    </div>
  );
}

/** A dollar amount saved when you leave the field (empty clears it). */
export function FeeField({ label, cents, onSave, disabled, suffix, id }: { label: string; cents: number | null | undefined; onSave: (cents: number | null) => Promise<unknown>; disabled?: boolean; suffix?: string; id: string }) {
  const shown = cents === null || cents === undefined ? "" : (cents / 100).toFixed(2);
  const [v, setV] = useState(shown);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setV(shown), [shown]);
  const commit = async () => {
    if (v.trim() === shown) return;
    let next: number | null = null;
    if (v.trim()) {
      try {
        next = parseDollars(v);
      } catch {
        setErr("Enter an amount like 450 or 1,250.00");
        return;
      }
      if (next < 0) return setErr("The fee can't be negative");
    }
    setErr(null);
    try {
      await onSave(next);
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <div className="space-y-1 text-sm">
      <label htmlFor={id} className="block text-xs text-muted-foreground">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative w-36">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
          <Input id={id} inputMode="decimal" className="pl-6 tabular" value={v} disabled={disabled} placeholder="Not set" onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
        </div>
        {suffix && <span className="text-xs text-muted-foreground">{suffix}</span>}
      </div>
      {err && <p className="text-xs text-destructive">{err}</p>}
    </div>
  );
}

export function money(cents: number | null | undefined, showCents = false) {
  return cents === null || cents === undefined ? "—" : formatCents(Math.round(cents), { showCents });
}

type CostRate = { userId: string; name: string; role: string; hourlyCostCents: number | null; weeklyHours: number };

/** Firm Settings › Team › Team and access › Time & costs (owner): the time tracker switch, and what each person's hour costs. */
export function TimeSettings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery<{ timeTracking?: boolean }>({ queryKey: ["/api/firm/settings"] });
  const { data: rates } = useQuery<CostRate[]>({ queryKey: ["/api/firm/cost-rates"] });
  const toggle = useMutation({
    meta: { inlineError: true },
    mutationFn: (timeTracking: boolean) => api.send("PATCH", "/api/firm/settings", { timeTracking }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/firm/settings"] });
      qc.invalidateQueries({ queryKey: RUNNING_KEY });
    },
  });
  const on = settings?.timeTracking === true;
  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-card-border bg-card p-5">
        <label className="flex items-start gap-3">
          <input type="checkbox" className="mt-0.5 h-4 w-4" checked={on} disabled={!settings || toggle.isPending} onChange={(e) => toggle.mutate(e.target.checked)} />
          <span>
            <span className="block text-sm font-medium">Track time on engagements</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Adds a timer and an "Add time" button to every engagement. Firm Analytics then shows each person's hours against their usual week, and the profit on each client. Switching it off keeps the time already logged.
            </span>
          </span>
        </label>
        {toggle.error && <p className="mt-2 text-sm text-destructive">{errorMessage(toggle.error)}</p>}
      </section>
      <section className="rounded-lg border border-card-border bg-card">
        <div className="border-b border-border bg-secondary/50 px-5 py-3">
          <h2 className="text-[15px] font-semibold">Staff cost rates</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">What an hour of each person's time costs the firm (pay, payroll taxes and benefits), and the hours they usually work in a week. Only you, as the owner, can see these and the profit worked out from them.</p>
        </div>
        {!rates ? null : (
          <ul className="divide-y divide-border">
            {rates.map((r) => (
              <CostRateRow key={r.userId} rate={r} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CostRateRow({ rate }: { rate: CostRate }) {
  const qc = useQueryClient();
  const [hours, setHours] = useState(String(rate.weeklyHours));
  const save = (hourlyCostCents: number | null, weeklyHours: number) =>
    api.send("PUT", `/api/firm/cost-rates/${rate.userId}`, { hourlyCostCents, weeklyHours }).then(() => qc.invalidateQueries({ queryKey: ["/api/firm/cost-rates"] }));
  const wh = Math.min(80, Math.max(1, Number(hours) || 40));
  return (
    <li className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{rate.name}</p>
        <p className="text-xs capitalize text-muted-foreground">{rate.role}</p>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <FeeField id={`rate-${rate.userId}`} label="Cost per hour" cents={rate.hourlyCostCents} onSave={(c) => save(c, wh)} />
        <label className="space-y-1 text-sm">
          <span className="block text-xs text-muted-foreground">Hours a week</span>
          <Input
            type="number"
            min={1}
            max={80}
            className="w-24 tabular"
            disabled={rate.hourlyCostCents === null}
            title={rate.hourlyCostCents === null ? "Enter the cost per hour first" : undefined}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            onBlur={() => wh !== rate.weeklyHours && save(rate.hourlyCostCents, wh)}
          />
        </label>
      </div>
    </li>
  );
}
